<template>
  <!-- The same styled autocomplete the Co-Lead "Add Missing UMR" modal uses, so
       the two manual-entry paths look and behave alike: a plain text box that
       offers what the system already holds as you type, while still accepting a
       UMR nobody has seen before - which is the point of this path. -->
  <CashCallAutocompleteInput
    :value="value"
    @input="$emit('input', $event)"
    label="UMR"
    name="UMR"
    required
    rules="required"
    :options="options"
    :max-options="1000"
    sort-options
  />
</template>

<script>
import { defineComponent, ref, onMounted } from '@vue/composition-api'

import CashCallAutocompleteInput from './CashCallAutocompleteInput.vue'
import CashCallService from '@services/CashCallService'

export default defineComponent({
  name: 'CashCallFormManualUMR',
  components: {
    CashCallAutocompleteInput
  },
  props: {
    value: String,
    // Scope of the key list - the same three the BDX-exists path sends.
    userCompanyId: [String, Number],
    userCompanyRole: String,
    userCompanyRoleValue: String
  },
  emits: ['input', 'update:keyPool'],
  setup(props, { emit }) {
    const options = ref([])

    const fetchUmrs = async () => {
      try {
        const response = await CashCallService.getBdxKeys({
          company_id: props.userCompanyId,
          role: props.userCompanyRole,
          role_value: props.userCompanyRoleValue,
          umr_criteria: 'Active',
          uw_option: 'Lead'
        })

        // Lincoln returns whole BDX keys (UMR_RC_CC_SN_CUR_YOA_TPA_CH); the UMR
        // is the first segment. Derived here rather than adding a Lincoln
        // endpoint - the same call already backs the BDX-exists path, so this
        // costs nothing new and stays scoped to what this user may see.
        //
        // The body is wrapped more than once (axios's response.data, then the
        // service envelope, then DRF's own), so walk down to the object that
        // actually carries UMR_Risk_Cat_Section rather than hard-coding a depth
        // - guessing it wrong is silent, leaving the field with no suggestions.
        let payload = response && response.data
        for (let depth = 0; depth < 4 && payload && !payload.UMR_Risk_Cat_Section; depth += 1) {
          payload = payload.data
        }

        const keys = (payload && payload.UMR_Risk_Cat_Section) || []
        options.value = Array.isArray(keys)
          ? [...new Set(
              keys
                .map((key) => String(key || '').split('_')[0])
                .filter(Boolean)
            )]
          : []

        // The whole keys, published upward. The modal needs them to find the
        // TPA/Coverholder behind a UMR_RC_CC_SN the user has typed, and this
        // is the only place they are fetched - re-requesting them there would
        // be a second identical call.
        emit('update:keyPool', Array.isArray(keys) ? keys : [])
      } catch (err) {
        // Suggestions are a convenience, not a gate: a failed lookup leaves an
        // ordinary text box, which is all this path strictly needs.
        console.error('Error fetching UMR suggestions', err)
      }
    }

    onMounted(fetchUmrs)

    return { options }
  }
})
</script>
